import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import moment from "moment";
import { FiPaperclip, FiPlus } from "react-icons/fi";
import { EmptyState, ItemCard, PageHeader } from "./DashboardUI";
import TicketDetailModal from "../modals/TicketDetailModal";
import Loader from "../loader/Loader";
import { checkCompanySubscription } from "../../redux/actions/SubscriptionActions";
import {
  createUserTickets,
  getTicketTypes,
  getUserTickets,
} from "../../redux/actions/TicketActions";
import { uploadTicketDocuments } from "../../redux/actions/DocumentActions";
import { addToCart, clearCart } from "../../redux/actions/CartActions";
import { clearServiceList } from "../../redux/actions/ServiceListActions";
import { clearRequest } from "../../redux/actions/RequestActions";
import { formatPrice } from "../../utils/formatPrice";

const TICKET_STATUS = {
  initiated: "En attente",
  in_progress: "En cours",
  fulfilled: "Terminée",
  failed: "Échouée",
};

const toast = (icon, title, text) =>
  Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 4000,
  }).fire({ icon, title, text });

// Page of a subscription service in the client space: its requests to our
// team when subscribed, otherwise an offer that starts the order flow
function ServiceSpace({
  serviceCode,
  flow,
  flowPath,
  title,
  lead,
  offer,
  price,
  isEligible = () => true,
  unavailableText,
  topContent,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authUser);
  const { activeCompany } = useSelector((state) => state.companies);
  const { ticketTypes, userTickets } = useSelector((state) => state.tickets);

  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: "", description: "" });
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const eligible = activeCompany && isEligible(activeCompany);

  useEffect(() => {
    if (!user?.token || !activeCompany?._id || !eligible) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setSubscribed(false);
    Promise.all([
      dispatch(getTicketTypes()),
      dispatch(
        checkCompanySubscription(
          user.token,
          activeCompany._id,
          serviceCode,
          ({ success, active, message }) => {
            if (success) setSubscribed(Boolean(active));
            else toast("error", "Échec", message);
          },
        ),
      ),
      dispatch(getUserTickets(user.token, activeCompany._id, serviceCode)),
    ]).finally(() => setLoading(false));
  }, [user?.token, activeCompany?._id, eligible, serviceCode, dispatch]);

  const typeName = (code) =>
    (ticketTypes || []).find((item) => item.code === code)?.name || "Demande";
  const serviceTypes = (ticketTypes || []).filter(
    (item) => item.service === serviceCode,
  );

  const tickets = (userTickets || []).filter((ticket) => {
    const term = search.trim().toLowerCase();
    return (
      (!status || ticket.status === status) &&
      (!term ||
        ticket.reference?.toLowerCase().includes(term) ||
        typeName(ticket.reclamationCode).toLowerCase().includes(term))
    );
  });

  // The order starts clean, for the active company
  const startSubscription = async () => {
    await dispatch(clearCart());
    await dispatch(clearServiceList());
    await dispatch(clearRequest());
    await dispatch(addToCart({ from: flow, company: activeCompany }));
    navigate(flowPath);
  };

  const closeForm = () => {
    setShowForm(false);
    setForm({ type: "", description: "" });
    setAttachment(null);
  };

  const handleCreated = async ({ success, message, data }) => {
    if (!success) {
      setSubmitting(false);
      toast("error", "Échec", message);
      return;
    }

    if (attachment) {
      const formData = new FormData();
      formData.append("documents", attachment);
      await dispatch(
        uploadTicketDocuments(user.token, data?.reference, formData, () => {}),
      );
    }

    setSubmitting(false);
    closeForm();
    toast("success", "Demande envoyée", message);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitting(true);
    dispatch(
      createUserTickets(
        user.token,
        {
          reclamationCode: form.type,
          company: activeCompany._id,
          description: form.description,
        },
        serviceCode,
        handleCreated,
      ),
    );
  };

  if (loading) {
    return <p className="dash-loading">Chargement…</p>;
  }

  if (!activeCompany) {
    return (
      <>
        <PageHeader title={title} lead={lead} />
        <div className="dash-card">
          <EmptyState title="Aucune entreprise active">
            Choisissez une entreprise en haut de la page, ou créez-en une.
          </EmptyState>
        </div>
      </>
    );
  }

  if (!eligible) {
    return (
      <>
        <PageHeader title={title} lead={lead} />
        <div className="dash-card">
          <EmptyState title="Service indisponible">
            {unavailableText}
          </EmptyState>
        </div>
      </>
    );
  }

  if (!subscribed) {
    return (
      <>
        <PageHeader title={title} lead={lead} />
        <div className="dash-card dash-offer">
          <div>
            <h2 className="dash-card-title">
              Ajoutez ce service à {activeCompany.businessName}
            </h2>
            <p className="dash-page-lead">{offer}</p>
            {price && (
              <p className="dash-offer-price">
                À partir de <strong>{formatPrice(price)}</strong> / mois
              </p>
            )}
          </div>
          <button
            type="button"
            className="dash-button"
            onClick={startSubscription}
          >
            Souscrire
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={submitting} />
      <PageHeader
        title={title}
        lead={`Vos demandes à notre équipe pour ${activeCompany.businessName}.`}
        actions={
          <button
            type="button"
            className="dash-button"
            onClick={() => setShowForm(true)}
          >
            <FiPlus aria-hidden="true" /> Nouvelle demande
          </button>
        }
      />

      {topContent}

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher une référence ou un type de demande"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Rechercher"
          />
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Statut"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(TICKET_STATUS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {tickets.length === 0 ? (
          <EmptyState title="Aucune demande">
            {userTickets?.length
              ? "Aucune demande ne correspond à votre recherche."
              : "Vos demandes à notre équipe apparaîtront ici."}
          </EmptyState>
        ) : (
          <div className="dash-items">
            {tickets.map((ticket) => (
              <ItemCard
                key={ticket.reference}
                title={typeName(ticket.reclamationCode)}
                meta={`#${ticket.reference} · ${moment(ticket.createdAt).format(
                  "DD/MM/YYYY",
                )}`}
                status={ticket.status}
                statusLabel={TICKET_STATUS[ticket.status]}
                actions={
                  <button
                    type="button"
                    className="dash-link-button"
                    onClick={() =>
                      setSelected({
                        ...ticket,
                        type: typeName(ticket.reclamationCode),
                      })
                    }
                  >
                    Voir
                  </button>
                }
              />
            ))}
          </div>
        )}
      </div>

      <Modal show={showForm} onHide={closeForm} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="dash-card-title mb-0">
            Nouvelle demande
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form className="dash-form" onSubmit={handleSubmit}>
            <label htmlFor="ticket-type">Type de demande</label>
            <select
              id="ticket-type"
              value={form.type}
              onChange={(event) =>
                setForm({ ...form, type: event.target.value })
              }
              required
            >
              <option value="">Choisissez le type</option>
              {serviceTypes.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name}
                </option>
              ))}
            </select>

            <label htmlFor="ticket-description">Description</label>
            <textarea
              id="ticket-description"
              rows={4}
              value={form.description}
              onChange={(event) =>
                setForm({ ...form, description: event.target.value })
              }
              placeholder="Décrivez votre besoin"
              required
            />

            <label htmlFor="ticket-file" className="dash-file">
              <FiPaperclip aria-hidden="true" />
              {attachment
                ? attachment.name
                : "Ajouter une pièce jointe (facultatif)"}
            </label>
            <input
              id="ticket-file"
              type="file"
              className="visually-hidden"
              onChange={(event) => setAttachment(event.target.files?.[0])}
            />

            <div className="dash-form-actions">
              <button
                type="button"
                className="dash-button is-secondary"
                onClick={closeForm}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="dash-button"
                disabled={submitting}
              >
                Envoyer
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <TicketDetailModal
        show={Boolean(selected)}
        onHide={() => setSelected(null)}
        ticket={selected}
      />
    </>
  );
}

export default ServiceSpace;
